import { Injectable, NgZone } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, exhaustMap, catchError, tap, withLatestFrom, mergeMap, filter, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import * as fromAccountActions from '../actions/account.actions';
import { Store, select } from '@ngrx/store';
import { AppState } from '../app.state';
import { ErrorService } from '../../services/error.service';
import { failedTransaction, setLoadingSpinner } from '../actions/shared.actions';
import { AlertService } from '../../services/alert.service';
import { AccountService } from 'src/app/services/account.service';
import { ModalController, NavController, ToastController } from '@ionic/angular';
import { RouterNavigatedAction, ROUTER_NAVIGATION } from '@ngrx/router-store';
import { APIResponse } from 'src/app/models/api-response.model';
import { getUserProfile } from '../actions/account.actions';
import { AuthService } from 'src/app/services/auth.service';
import { SharedService } from 'src/app/services/shared.service';
import { UserProfile } from 'src/app/models/user-profile.model';
import { NavigationExtras } from '@angular/router';
import * as fromOrganizationActions from '../actions/organization.actions';
import { ContactService } from 'src/app/services/contact.service';
import { RideService } from 'src/app/services/ride.service';
import { OfflineStorageService } from 'src/app/services/offline-storage.service';
import { ResendSuccessComponent } from 'src/app/shared/modals/resend-success/resend-success.component';
import { AddContactSuccessComponent } from 'src/app/shared/modals/add-contact-success/add-contact-success.component';
import { ChangePasswordSuccessComponent } from 'src/app/shared/modals/change-password-success/change-password-success.component';
import { getActiveSendee } from '../selectors/organization.selectors';
import { CompleteProfileSuccessComponent } from 'src/app/shared/modals/complete-profile-success/complete-profile-success.component';
import { UpdateProfileSuccessComponent } from 'src/app/shared/modals/update-profile-success/update-profile-success.component';
import { RegistrationSuccessComponent } from 'src/app/shared/modals/registration-success/registration-success.component';
import { DynamicSuccessComponent } from 'src/app/shared/modals/dynamic-success/dynamic-success.component';
import { VerificationComponent } from 'src/app/shared/modals/verification/verification.component';
import { SendPointsSuccessComponent } from 'src/app/shared/modals/send-points-success/send-points-success.component';
import { autoLogout } from '../actions/auth.actions';
import { DynamicErrorComponent } from 'src/app/shared/modals/dynamic-error/dynamic-error.component';
@Injectable()
export class AccountEffects {

  
  activeSendee$ : Observable<any>;

  getUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountActions.getUserProfile),
      mergeMap(() => {
        return this.accountService.userProfile().pipe(
          map((response: APIResponse) => {           
            console.log('userProfile$', response);
            return fromAccountActions.updateUserProfileSuccess({ data: response.details});
          })
        );
      })
    )
  );

  userProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/bottom-tabs/dashboard');
      }),
      mergeMap(() => {
        return this.accountService.userProfile().pipe(
          map((response: APIResponse) => {
            console.log('userProfile$', response);
            return fromAccountActions.updateUserProfileSuccess({ data: response.details});
          })
        );
      })
    );
  });

  scanQr$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountActions.scanQr),
      exhaustMap((action) =>
        this.accountService.scanQr(action.data).pipe(
          map((response) => {
            console.log('scanQr$', response, action);
            this.store.dispatch(setLoadingSpinner({status: false}));
            this.store.dispatch(fromAccountActions.scanQr({data: action.data, sendWith: action?.sendWith}));         
            return fromAccountActions.scanQrSuccess({data: response?.details, sendWith: action?.sendWith});
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            this.navCtrl.navigateBack('/home/bottom-tabs/dashboard');
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  scanQrSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAccountActions.scanQrSuccess),
        withLatestFrom(this.store.pipe(select(getActiveSendee))),
        tap(([action, activeSendee]) => {
          console.log('scanQrSuccess$', action.data);

          this.store.dispatch(fromOrganizationActions.setActiveSendee({data : {
            ...activeSendee.data,
            contacts : action.data
          }}));

          const navigationExtras: NavigationExtras = {
            queryParams: {
              userData: action.data,
              sendWith: action?.sendWith
            }
          }
          this.navCtrl.navigateForward('/home/send-points', navigationExtras);
        })
      ),
    { dispatch: false }
  );

  confirmPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountActions.confirmChangePassword),
      exhaustMap((action) =>
        this.authService.forgotPassword(action).pipe(
          map((response) => {
            console.log(response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAccountActions.confirmChangePasswordSuccess({
              data: {
                username: action.username,
              },
            });
          }),
          catchError((response) => {
            console.error('confirmPassword$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  confirmPasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAccountActions.confirmChangePasswordSuccess),
        tap(async (action) => {
          console.log('changePasswordSuccess$', action.data);
          if(action.data){
            const navigationExtras: NavigationExtras = {
              queryParams: {
                username: action?.data?.username
              },
              replaceUrl: false
            }
            this.navCtrl.navigateForward('/home/change-password', navigationExtras);
            const modal = await this.modalController.create({
              component: ResendSuccessComponent,
              cssClass: 'resend-success-modal',
            });
            modal.present();
            setTimeout(() => {
              modal.dismiss();
            }, 2000);
          }
        })
      ),
    { dispatch: false }
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountActions.changePassword),
      exhaustMap((action) =>
        this.authService.forgotPasswordConfirm(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAccountActions.changePasswordSuccess({ data: response });
          }),
          catchError((response) => {
            console.error('confirmPassword$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  changePasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAccountActions.changePasswordSuccess),
        tap(async(action) => {
          console.log('confirmSuccess$', action.data);
          const modal = await this.modalController.create({
            component: ChangePasswordSuccessComponent,
            cssClass: 'delete-account-confirm'
          });

          modal.onDidDismiss().then((res) => {
            this.navCtrl.navigateBack('/home/bottom-tabs/settings');
          });
          return modal.present();
        })
      ),
    { dispatch: false }
  );

  sendPoints$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountActions.sendPoints),
      exhaustMap((action) =>
        this.accountService.sendPoints(action.data).pipe(
          map((response) => { 
            console.log('sendPoints$', action.data);
            this.store.dispatch(setLoadingSpinner({status: false}));
            return fromAccountActions.sendPointsSuccess({data: response?.details, payload: action?.data});
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  sendPointsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAccountActions.sendPointsSuccess),
        tap(async (action) => {
          console.log('sendPointsSuccess$', action);
          if(!action?.payload?.isConfirm){
            const navigationExtras: NavigationExtras = {
              queryParams: {
                payload: action?.payload,
                userDetails: action?.data?.receiver
              }
            }
            this.navCtrl.navigateForward('/home/confirm-send-points', navigationExtras);
          }
          else{
            this.store.dispatch(fromOrganizationActions.getUserOrganizationPoints());
            this.navCtrl.navigateForward('/home/bottom-tabs/transaction-history');
            const modal = await this.modalController.create({
              component: SendPointsSuccessComponent,
              componentProps: {
                details: action?.data,
                payload: action?.payload
              }
            });
        
            return modal.present();
          }
        })
      ),
    { dispatch: false }
  );

  updateUserProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAccountActions.updateUserProfileSuccess),
        tap(async (action) => {
          console.log('updateUserProfileSuccess$', action.data);
          const user: UserProfile = action.data;
        })
      ),
    { dispatch: false }
  );

  completeProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountActions.completeProfile),
      exhaustMap((action) =>
        this.accountService.completeProfile(action.data).pipe(
          map((response) => { 
            this.store.dispatch(setLoadingSpinner({status: false}));
            this.store.dispatch(getUserProfile());         
            return fromAccountActions.completeProfileSuccess( response );
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  completeProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAccountActions.completeProfileSuccess),
        tap((action) => {
          this.navCtrl.navigateForward('/home/bottom-tabs/dashboard');
          // this.alertService.showInfoMessage('Profile Updated!');
          this.completeSuccessModal();
          console.log('completeProfileSuccess$', action.data);
        })
      ),
    { dispatch: false }
  );

  async updateSuccessModal(){
    const modal = await this.modalController.create({
      component: UpdateProfileSuccessComponent,
      cssClass: 'delete-account-confirm',
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      initialBreakpoint: 1,
      breakpoints: [0, 0.5, 1],
      handle: false
    });

    return modal.present();
  }

  async registrationSuccessModal(){
    const modal = await this.modalController.create({
      component: RegistrationSuccessComponent,
      cssClass: 'delete-account-confirm',
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      initialBreakpoint: 1,
      breakpoints: [0, 0.5, 1],
      handle: false
    });
  }

  async completeSuccessModal(){
    const modal = await this.modalController.create({
      component: CompleteProfileSuccessComponent,
      cssClass: 'delete-account-confirm',
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      initialBreakpoint: 1,
      breakpoints: [0, 0.5, 1],
      handle: false
    });

    return modal.present();
  }

  updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountActions.editProfile),
      exhaustMap((action) =>
        this.accountService.editProfile(action.data).pipe(
          map((response) => { 
            console.log('updateProfile$', action.data);

            this.navCtrl.navigateForward('/home/settings/profile-information');
            this.updateSuccessModal();

            this.store.dispatch(setLoadingSpinner({status : false}));
            this.store.dispatch(getUserProfile());
            
            return fromAccountActions.getUserProfileSuccess(response);   
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  updateProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAccountActions.editProfileSuccess),
        tap((action) => {
          this.navCtrl.navigateForward('/home/bottom-tabs/dashboard');
          this.updateSuccessModal();
          console.log('updateProfileSuccess$', action.data);
        })
      ),
    { dispatch: false }
  );

  reloadUserContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountActions.reloadUserContact),
      mergeMap(() => {
        return this.contactService.getContacts().pipe(
          map((response) => {           
            console.log('reloadUserContact$', response);
            return fromAccountActions.getUserContactSuccess({ data: response.details});
          })
        );
      })
    )
  );

  getContacts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/send-points') 
        || r.payload.routerState.url.startsWith('/home/bottom-tabs/calendar')
      }),
      mergeMap(() => {
        return this.contactService.getContacts().pipe(
          map((response: APIResponse) => {           
            console.log('getContacts$', response);
            return fromAccountActions.getUserContactSuccess({ data: response.details});
          })
        );
      })
    )
  });

  getTransactions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/bottom-tabs/transaction-history')
      }),
      mergeMap(() => {
        return this.accountService.transactions().pipe(
          map((response: APIResponse) => {           
            console.log('getTransactions$', response);
            return fromAccountActions.getTransactionSuccess({ data: response.details});
          })
        );
      })
    )
  });

  resendConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountActions.resendOtp),
      exhaustMap((action) =>
        this.authService.resendVerificationCode(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAccountActions.resendOtpSuccess({ data: response });
          }),
          catchError((response) => {
            console.error('resendConfirmation$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  semiVerificationEmail$ = createEffect(() => 
    this.actions$.pipe(
      ofType(fromAccountActions.semiVerificationEmail),
      exhaustMap((action) => 
        this.authService.updateEmail(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({status: false}))
            return fromAccountActions.semiVerificationEmailSuccess({data:response });
          }),
          catchError((response) => {
            console.error('semiVerificationEmail$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  semiVerificationEmailSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAccountActions.semiVerificationEmailSuccess),
        tap(async(action) => {
          console.log('semiVerificationEmailSuccess$', action.data);

          const modal = await this.modalController.create({
            component: VerificationComponent,
            cssClass: 'verification-modal'
          });

         return modal.present();
        })
      ),
    { dispatch: false }
  );

  confirmEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountActions.confirmEmail),
      exhaustMap((action) =>
        this.authService.confirmEmail(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAccountActions.confirmEmailSuccess({ data: response });
          }),
          catchError((response) => {
            console.error('confirmEmail$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            this.failedModal('An error occured', error?.error)
            return of(fromAccountActions.confirmEmailFailed({ data: error }));
          })
        )
      )
    )
  );

  confirmEmailSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAccountActions.confirmEmailSuccess),
        tap(async (action) => {
          if(action?.data){
            console.log('confirmEmail$', action?.data);
            this.store.dispatch(getUserProfile());         
            const activeModal = await this.modalController.getTop();
            if(activeModal){
              this.modalController.dismiss();
            }
            // this.successModal("Success", "Your email address have been successfully verified. Proceed to phone number verification.", "Proceed");
            this.successModal("Success", "Your email address have been successfully verified.", "Proceed");
            this.navCtrl.navigateBack('home/bottom-tabs/dashboard');
          }
        })
      ),
    { dispatch: false }
  );

  deleteAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccountActions.deleteAccount),
      exhaustMap((action) =>
        this.accountService.deleteAccount(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAccountActions.deleteAccountSuccess({ data: response });
          }),
          catchError((response) => {
            console.error('deleteAccount$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(failedTransaction({ data: error }));
          })
        )
      )
    )
  );

  deleteAccountSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAccountActions.deleteAccountSuccess),
        tap(async(action) => {
          console.log('deleteAccountSuccess$', action.data);
          this.modalController.dismiss();
          this.store.dispatch(autoLogout());
          this.presentToast('Successfully requested for deletion', 'middle', 'toastSuccess');
        })
      ),
    { dispatch: false }
  );

  async presentToast(msg, pos, style) {
    const toast = await this.toastController.create({
      message: msg,
      duration: 3000,
      position: pos,
      cssClass: style
    });
    toast.present();
  }

  async successModal(title: string, content: string, okButtonLabel: string){
    const modal = await this.modalController.create({
      component: DynamicSuccessComponent,
      componentProps: {
        title: title,
        content: content,
        okButtonLabel: okButtonLabel
      },
      cssClass: 'delete-account-confirm',
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      initialBreakpoint: 1,
      breakpoints: [0, 0.5, 1],
      handle: false
    });

    return modal.present();
  }

  async failedModal(title: string, content: string){
    console.log('failed modal')
    const modal = await this.modalController.create({
      component: DynamicErrorComponent,
      componentProps: {
        title: title,
        content: content
      },
      cssClass: 'delete-account-confirm',
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      initialBreakpoint: 1,
      breakpoints: [0, 0.5, 1],
      handle: false
    });

    return modal.present();
  }
  
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private errorService: ErrorService,
    private alertService: AlertService,
    private accountService: AccountService,
    private contactService: ContactService,
    private modalController: ModalController,
    private navCtrl: NavController,
    private authService : AuthService,
    private sharedService: SharedService,
    private rideService: RideService,
    private ngZone: NgZone,
    private toastController: ToastController,
    private storage: OfflineStorageService
  ) {}
}
